import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { Select } from '../../../components/ui/Select.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { categoriesApi } from '../../../api/categories.js';
import { ApiError } from '../../../api/client.js';

// Build a category slug from a free-text name, matching the server pattern
// /^[a-z0-9](-?[a-z0-9])*$/ (lowercase, accents stripped, single hyphens).
function slugify(name) {
  return String(name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Inline "create a new category" row: a full-width name field with the
// confirm/cancel actions right-aligned underneath, so nothing overflows the
// narrow grid cell. Used for both species and breeds so the operator never
// leaves the document form.
function InlineCreate({ label, placeholder, onCreate, onCancel, pending }) {
  const [name, setName] = useState('');
  const submit = () => { if (name.trim()) onCreate(name.trim()); };
  return (
    <div className="mt-2 flex flex-col gap-2">
      <Input
        label={label}
        value={name}
        autoFocus
        placeholder={placeholder}
        autoComplete="off"
        data-1p-ignore
        data-lpignore="true"
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); submit(); }
        }}
      />
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" onClick={onCancel} disabled={pending}>Annulla</Button>
        <Button type="button" size="sm" disabled={!name.trim() || pending} onClick={submit}>
          {pending ? '...' : 'Crea'}
        </Button>
      </div>
    </div>
  );
}

/**
 * Controlled species/breed picker. The parent owns `speciesId` / `breedId`
 * (typically two react-hook-form fields) and gets a single `onChange` with the
 * next pair. Operators can pick from existing categories or create new ones
 * on the spot; the server turns the selection into the correct RAG tags.
 */
export function TaxonomyPicker({ open, speciesId, breedId, onChange }) {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(null); // 'species' | 'breed' | null

  const speciesQuery = useQuery({
    queryKey: ['cat', 'species'],
    enabled: open,
    queryFn: () => categoriesApi.list({ kind: 'species', limit: 100, active: true })
  });
  const breedQuery = useQuery({
    queryKey: ['cat', 'breed'],
    enabled: open,
    queryFn: () => categoriesApi.list({ kind: 'breed', limit: 100, active: true })
  });

  const speciesOpts = useMemo(() => ((speciesQuery.data && speciesQuery.data.items) || []), [speciesQuery.data]);
  const breedOpts = useMemo(() => ((breedQuery.data && breedQuery.data.items) || []), [breedQuery.data]);
  const breedsForSpecies = useMemo(
    () => breedOpts.filter((b) => String(b.parentId?._id || b.parentId || '') === String(speciesId || '')),
    [breedOpts, speciesId]
  );

  // When editing a document linked to a breed, only the breed id is known up
  // front: derive its species so the breed dropdown becomes relevant.
  useEffect(() => {
    if (!breedId || speciesId) return;
    const breed = breedOpts.find((b) => String(b._id) === String(breedId));
    const parent = breed && (breed.parentId?._id || breed.parentId);
    if (parent) onChange({ speciesId: String(parent), breedId: String(breedId) });
  }, [breedId, speciesId, breedOpts, onChange]);

  const createMutation = useMutation({
    mutationFn: ({ kind, name, parentId }) => categoriesApi.create({ kind, name, slug: slugify(name), parentId: parentId || null }),
    onSuccess: async (created, vars) => {
      const item = created && (created._id ? created : created.data);
      await queryClient.invalidateQueries({ queryKey: ['cat', vars.kind] });
      if (item && item._id) {
        if (vars.kind === 'species') onChange({ speciesId: String(item._id), breedId: '' });
        else onChange({ speciesId, breedId: String(item._id) });
      }
      setCreating(null);
      toast.success(vars.kind === 'species' ? 'Specie creata.' : 'Razza creata.');
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Creazione non riuscita.')
  });

  return (
    <div className="rounded-md border border-border bg-surface-subtle p-3">
      <p className="mb-2 text-sm font-medium text-ink">Specie / razza</p>
      <p className="mb-3 text-xs text-ink-muted">
        Collega il documento a una specie (e a una razza, se specifica). I tag vengono impostati in automatico.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <Select
            label="Specie"
            value={speciesId || ''}
            onChange={(e) => onChange({ speciesId: e.target.value, breedId: '' })}
          >
            <option value="">Nessuna</option>
            {speciesOpts.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
          </Select>
          {creating === 'species' ? (
            <InlineCreate
              label="Nome nuova specie"
              placeholder="es. Coniglio"
              pending={createMutation.isPending}
              onCancel={() => setCreating(null)}
              onCreate={(name) => createMutation.mutate({ kind: 'species', name })}
            />
          ) : (
            <button type="button" className="mt-1 text-xs text-ink-muted underline hover:text-ink" onClick={() => setCreating('species')}>
              + Nuova specie
            </button>
          )}
        </div>

        <div>
          <Select
            label="Razza (opzionale)"
            value={breedId || ''}
            disabled={!speciesId}
            onChange={(e) => onChange({ speciesId, breedId: e.target.value })}
          >
            <option value="">Nessuna</option>
            {breedsForSpecies.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
          </Select>
          {speciesId && creating === 'breed' ? (
            <InlineCreate
              label="Nome nuova razza"
              placeholder="es. Ariete nano"
              pending={createMutation.isPending}
              onCancel={() => setCreating(null)}
              onCreate={(name) => createMutation.mutate({ kind: 'breed', name, parentId: speciesId })}
            />
          ) : speciesId ? (
            <button type="button" className="mt-1 text-xs text-ink-muted underline hover:text-ink" onClick={() => setCreating('breed')}>
              + Nuova razza
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
